// 員工個資編輯抽屜 (V243)。 兩種模式共用一個表單：
//   mode='self' — 本人自助改自己的聯絡資料 (① 基本個資)，走 /me/profile。
//   mode='hr'   — 人資/系統管理員 改任一員工全部 (①②③)，走 /members/:id/profile。
// ③ 高敏感 (身分證/薪資帳號)：只顯示遮罩，輸入框留空=不變、輸入新值=更新 (後端加密)。
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useActor } from '../lib/actor-store.ts';
import { type ApiError, api } from '../lib/api.ts';
import { useStaff } from '../lib/use-approvals.ts';
import {
  type EmployeeProfile,
  type HrProfilePatch,
  useMemberProfile,
  useMyProfile,
  useUpdateMemberProfile,
  useUpdateMyProfile,
} from '../lib/use-employee-profile.ts';
import { ChangePasswordForm } from './ChangePasswordForm.tsx';
import { Drawer } from './Drawer.tsx';
import { EmployeeDocuments } from './EmployeeDocuments.tsx';
import { EmploymentCertificateDocument } from './EmploymentCertificateDocument.tsx';
import { ErrorBlock } from './ErrorBlock.tsx';
import { LoadingPlaceholder } from './LoadingPlaceholder.tsx';
import {
  BankFields,
  type BankFieldsValue,
  isValidAccountNumber,
  normalizeAccountNumber,
} from './form/BankFields.tsx';
import { DateInput } from './form/DateInput.tsx';
import { Field as SharedField, inputClass } from './form/Field.tsx';
import { labelClass } from './form/FormSection.tsx';
import { StaffOptions } from './form/StaffOptions.tsx';

// 2026-07-20: 原本是手抄一份 inputClass 但**漏掉 disabled: variants** → 通訊地址
// 勾「同戶籍」後 disabled 了卻不會灰掉, 看起來還能打字、打了沒反應。
// 改用共用常數 (§4.5 就是為了防這個)。
const inputCls = inputClass;
// 2026-07-20: 字串本來就跟共用的 labelClass 逐字相同 — 改成引用同一份 (鐵則 3)。
const labelCls = labelClass;

/**
 * 2026-08-17 使用者:「文件那些可以不用這邊 show，但我點下去要一個地方可以看到」
 * —— 缺件徽章從成員名冊移到這裡。 狀態直接由名冊那一列傳進來 (它本來就有這三個
 * 欄位), 不另外打一支 API: 少一次往返, 也不會出現「名冊說缺、抽屜說有」的分岔。
 */
export interface DocStatus {
  /** 無到職日 = 非受僱員工, 三個徽章都不適用 */
  hireDate: string | null;
  hasLaborContract: boolean;
  hasInsurance: boolean;
  insurancePendingFrom: string | null;
  hasSalaryAccount: boolean;
  /** V376: 合夥人 / 顧問等非受僱身分 —— 缺件檢查不適用。 */
  docExempt?: boolean;
  docExemptReason?: string | null;
}

type Mode =
  | { kind: 'self' }
  | { kind: 'hr'; userId: number; memberName: string; docStatus?: DocStatus };

// 員工缺件提示徽章:有(綠✓) / 待生效(琥珀) / 缺(紅)。
// pending: 有紀錄但尚未生效 (例:未來生效的加保) → 既不是「已完成✓」也不是「缺」。
function DocBadge({ ok, label, pending }: { ok: boolean; label: string; pending?: string | null }) {
  if (ok)
    return (
      <span className="px-1.5 py-0.5 text-[11px] rounded-full ring-1 bg-emerald-50 text-emerald-700 ring-emerald-200">
        {label} ✓
      </span>
    );
  if (pending)
    return (
      <span className="px-1.5 py-0.5 text-[11px] rounded-full ring-1 bg-amber-50 text-amber-700 ring-amber-200">
        {label} {pending} 生效
      </span>
    );
  return (
    <span className="px-1.5 py-0.5 text-[11px] rounded-full ring-1 bg-red-50 text-red-700 ring-red-200">
      {label} 缺
    </span>
  );
}

// ① 基本個資 表單狀態 (兩模式共用)
type ContactState = {
  birth_date: string;
  gender: string;
  mobile_phone: string;
  home_phone: string;
  mailing_address: string;
  registered_address: string;
  emergency_contact_name: string;
  emergency_contact_relation: string;
  emergency_contact_phone: string;
};
// ② 雇用 + ③ 明文欄 (hr)
type HrState = {
  hire_date: string;
  termination_date: string;
  termination_reason: string;
  department: string;
  doc_exempt_reason: string;
  manager_user_id: string; // app_user id (字串;空=無)
  salary_bank_name: string;
  salary_bank_branch: string;
  salary_account_holder: string;
};

const emptyContact: ContactState = {
  birth_date: '',
  gender: '',
  mobile_phone: '',
  home_phone: '',
  mailing_address: '',
  registered_address: '',
  emergency_contact_name: '',
  emergency_contact_relation: '',
  emergency_contact_phone: '',
};
const emptyHr: HrState = {
  hire_date: '',
  termination_date: '',
  termination_reason: '',
  department: '',
  doc_exempt_reason: '',
  manager_user_id: '',
  salary_bank_name: '',
  salary_bank_branch: '',
  salary_account_holder: '',
};

const s = (v: string | null) => v ?? '';
const orNull = (v: string) => (v.trim() ? v.trim() : null);

export function EmployeeProfileDrawer({
  mode,
  onClose,
  accountPanel,
  initialTab,
}: {
  mode: Mode;
  onClose: () => void;
  /**
   * 2026-08-22: 「帳號與權限」分頁的內容 (email / 職級 / 能力 / 啟用 / 離職)。
   * 由 MembersTab 傳進來 —— 那些欄位的 mutation / 權限判斷都住在那邊, 搬過來只會
   * 把同一份邏輯抄第二份。 只有 hr 模式吃這個 prop。
   *
   * ⚠ 它**自己帶送出/取消按鈕** (跟薪轉/文件分頁一樣), 所以下面 footer 的白名單
   *   維持 `tab === 'info'` —— 不要為了它放寬, 否則會出現兩排按鈕。
   * ⚠ 手機不傳這個 prop (Phase 1G 唯讀) → 分頁根本不出現, 不需要唯讀版的表單。
   */
  accountPanel?: React.ReactNode;
  /** 開啟時停在哪一個分頁。 預設 'info'。 */
  initialTab?: 'account' | 'info';
}) {
  const actor = useActor((a) => a.actor);
  const isHr = mode.kind === 'hr';

  const myQ = useMyProfile(actor);
  const memberQ = useMemberProfile(actor, isHr ? mode.userId : null);
  const profileQ = isHr ? memberQ : myQ;

  const updateMe = useUpdateMyProfile(actor);
  const updateMember = useUpdateMemberProfile(actor, isHr ? mode.userId : 0);
  const staffQ = useStaff(actor); // 直屬主管下拉(hr 模式用)

  const [contact, setContact] = useState<ContactState>(emptyContact);
  const [hr, setHr] = useState<HrState>(emptyHr);
  // V376 —— 布林值刻意**不放進 HrState**: hField 是泛型 (value: hr[k]), 混進 boolean
  // 會把每一個欄位的 value 型別撐成 string|boolean, 全部 input 一起壞。
  const [docExempt, setDocExempt] = useState(false);
  // 通訊地址同戶籍 — 勾選時自動帶入 + 鎖定通訊欄; 戶籍變動時跟著同步。
  const [sameAsReg, setSameAsReg] = useState(false);
  // ③ 加密欄輸入 (留空=不變)
  const [nationalId, setNationalId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [showCert, setShowCert] = useState(false);
  const [tab, setTab] = useState<'account' | 'info' | 'salary' | 'docs' | 'security'>(
    initialTab === 'account' && accountPanel ? 'account' : 'info',
  );
  // 2026-07-20 (使用者:「身分資料對於 admin 不 mask」): 按一下才解密。
  // 不在開抽屜時就帶明文 —— 多數情況只是要看電話/到職日, 沒必要讓身分證進到
  // React Query cache 跟瀏覽器記憶體。 每次按下都會寫一筆 read_pii 稽核。
  const [revealed, setRevealed] = useState<{
    national_id: string | null;
    salary_account: string | null;
  } | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [revealErr, setRevealErr] = useState<string | null>(null);

  async function revealPii() {
    if (!isHr) return;
    setRevealing(true);
    setRevealErr(null);
    try {
      setRevealed(
        await api<{ national_id: string | null; salary_account: string | null }>(
          `/api/v1/members/${mode.userId}/profile/pii`,
          { actor },
        ),
      );
    } catch (e) {
      setRevealErr((e as ApiError)?.message ?? '讀取失敗');
    } finally {
      setRevealing(false);
    }
  }

  const p: EmployeeProfile | null | undefined = profileQ.data;
  // 載入後把現值填進表單
  useEffect(() => {
    if (!p) return;
    setContact({
      birth_date: s(p.birth_date),
      gender: s(p.gender),
      mobile_phone: s(p.mobile_phone),
      home_phone: s(p.home_phone),
      mailing_address: s(p.mailing_address),
      registered_address: s(p.registered_address),
      emergency_contact_name: s(p.emergency_contact_name),
      emergency_contact_relation: s(p.emergency_contact_relation),
      emergency_contact_phone: s(p.emergency_contact_phone),
    });
    // 兩地址已相同 (且非空) → 預設勾「同戶籍」
    setSameAsReg(!!p.registered_address && s(p.mailing_address) === s(p.registered_address));
    setHr({
      hire_date: s(p.hire_date),
      termination_date: s(p.termination_date),
      termination_reason: s(p.termination_reason),
      department: s(p.department),
      doc_exempt_reason: s(p.doc_exempt_reason),
      manager_user_id: p.manager_user_id ? String(p.manager_user_id) : '',
      salary_bank_name: s(p.salary_bank_name),
      salary_bank_branch: s(p.salary_bank_branch),
      salary_account_holder: s(p.salary_account_holder),
    });
    setDocExempt(p.doc_exempt === true);
  }, [p]);

  // 勾「同戶籍」時, 戶籍地址變動 → 通訊地址跟著同步。
  const regAddr = contact.registered_address;
  useEffect(() => {
    if (!sameAsReg) return;
    setContact((c) => (c.mailing_address === regAddr ? c : { ...c, mailing_address: regAddr }));
  }, [sameAsReg, regAddr]);

  const cField = (k: keyof ContactState) => ({
    value: contact[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setContact((c) => ({ ...c, [k]: e.target.value })),
  });
  const hField = (k: keyof HrState) => ({
    value: hr[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setHr((h) => ({ ...h, [k]: e.target.value })),
  });

  const saving = updateMe.isPending || updateMember.isPending;

  async function submit() {
    setErr(null);
    const contactPatch = {
      birth_date: orNull(contact.birth_date),
      mobile_phone: orNull(contact.mobile_phone),
      home_phone: orNull(contact.home_phone),
      mailing_address: orNull(contact.mailing_address),
      registered_address: orNull(contact.registered_address),
      emergency_contact_name: orNull(contact.emergency_contact_name),
      emergency_contact_relation: orNull(contact.emergency_contact_relation),
      emergency_contact_phone: orNull(contact.emergency_contact_phone),
    };
    try {
      if (!isHr) {
        await updateMe.mutateAsync(contactPatch);
      } else {
        const patch: HrProfilePatch = {
          ...contactPatch,
          hire_date: orNull(hr.hire_date),
          termination_date: orNull(hr.termination_date),
          termination_reason: orNull(hr.termination_reason),
          department: orNull(hr.department),
          doc_exempt: docExempt,
          doc_exempt_reason: orNull(hr.doc_exempt_reason),
          manager_user_id: hr.manager_user_id ? Number(hr.manager_user_id) : null,
          salary_bank_name: orNull(hr.salary_bank_name),
          salary_bank_branch: orNull(hr.salary_bank_branch),
          salary_account_holder: orNull(hr.salary_account_holder),
        };
        // ③ 只在輸入了新值時才送 (留空 = 不變, 不會清掉現有加密值)
        if (nationalId.trim()) patch.national_id = nationalId.trim();
        await updateMember.mutateAsync(patch);
      }
      onClose();
    } catch (e) {
      setErr((e as ApiError)?.message ?? '儲存失敗');
    }
  }

  const basicFields = (
    <>
      <Field label="出生日期">
        <DateInput className={inputCls} {...cField('birth_date')} />
      </Field>
      <Field label="手機">
        <input className={inputCls} {...cField('mobile_phone')} />
      </Field>
      <Field label="市話 / 分機">
        <input className={inputCls} {...cField('home_phone')} />
      </Field>
      <Field label="戶籍地址" full>
        <input className={inputCls} {...cField('registered_address')} />
      </Field>
      {/* 這格不能用 <Field>: 它現在會用 <label> 包住 children, 而這裡的 children
          自己就有一個 <label> (同戶籍勾選框) — 巢狀 label 是無效 HTML。
          改成顯式 htmlFor 關聯, 視覺順序不變。 */}
      <div className="sm:col-span-2">
        <label className={labelCls} htmlFor="emp-mailing-address">
          通訊地址
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500 mb-1 select-none">
          <input
            type="checkbox"
            checked={sameAsReg}
            onChange={(e) => {
              setSameAsReg(e.target.checked);
              if (e.target.checked)
                setContact((c) => ({ ...c, mailing_address: c.registered_address }));
            }}
          />
          同戶籍地址
        </label>
        <input
          id="emp-mailing-address"
          className={inputCls}
          disabled={sameAsReg}
          {...cField('mailing_address')}
        />
      </div>
      <Field label="緊急聯絡人">
        <input className={inputCls} {...cField('emergency_contact_name')} />
      </Field>
      <Field label="關係">
        <input className={inputCls} {...cField('emergency_contact_relation')} />
      </Field>
      <Field label="緊急聯絡電話">
        <input className={inputCls} {...cField('emergency_contact_phone')} />
      </Field>
    </>
  );

  // 2026-09-06 使用者:「這邊可以排版好看一點嗎」。 原本的欄位順序是
  //   到職日 | 離職日 / 離職原因 | 部門 / 缺件檢查 | 直屬主管
  // 兩個問題:
  //   (1) **配對配錯** —— 「離職原因」被「部門」隔開它真正的鄰居(離職日);
  //       而「部門/直屬主管」明明是同一組(組織歸屬)卻被拆到不同列。
  //   (2) **高度差太大** —— 「缺件檢查」是 checkbox + 條件輸入 + 說明三層,
  //       右邊配一個單行下拉 → 右欄大片空白, 而那行說明看起來像浮在欄位外面。
  // 改成按「這個欄位在講什麼」分組, 高的那塊自己整列:
  //   在職期間: 到職日 | 離職日 → 離職原因(整列)
  //   組織:     部門   | 直屬主管
  //   例外設定: 缺件檢查(整列)
  const employmentFields = (
    <>
      <Field label="到職日期">
        <DateInput className={inputCls} {...hField('hire_date')} />
      </Field>
      <Field label="離職日期">
        <DateInput className={inputCls} {...hField('termination_date')} />
      </Field>
      {/* 緊跟著離職日 —— 它只在有離職日時才有意義, 中間插別的欄位會讀不出關聯 */}
      <Field label="離職原因" full>
        <input
          className={inputCls}
          placeholder="自願離職 / 資遣 / 退休…"
          {...hField('termination_reason')}
        />
      </Field>
      <Field label="部門">
        <input className={inputCls} {...hField('department')} />
      </Field>
      <Field label="直屬主管">
        <select className={inputCls} {...hField('manager_user_id')}>
          <StaffOptions q={staffQ} placeholder="— 無 —" exclude={isHr ? mode.userId : 0} />
        </select>
      </Field>
      {/* V376 —— 使用者:「到職日可以取消嗎?」 起因是合夥人被追勞動契約/投保/薪轉。
          清到職日確實能關掉提示, 但那會連帶讓特休算不出年資、薪資與離職失去基準。
          改成獨立旗標: 到職日照留 (那是事實), 只把缺件檢查標成不適用。
          2026-09-06: 移到最後並佔滿整列 —— 它是三層高的例外設定, 跟上面那些
          「雇用事實」不同性質, 擠在半欄裡會把右欄壓出一片空白。 */}
      <Field label="缺件檢查" group full>
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded"
              checked={docExempt}
              onChange={(e) => setDocExempt(e.target.checked)}
            />
            <span className="text-slate-700">不需備件（合夥人 / 顧問等非受僱身分）</span>
          </label>
          {docExempt && (
            <input
              className={`${inputCls} mt-2`}
              placeholder="原因（例：合夥人，非受僱）— 勞檢/查帳要答得出來"
              value={hr.doc_exempt_reason}
              onChange={(e) => setHr({ ...hr, doc_exempt_reason: e.target.value })}
            />
          )}
          <p className="text-[11px] text-slate-500 mt-2">
            只影響缺件提示。到職日、特休年資、薪資與離職流程都不受影響。
          </p>
        </div>
      </Field>
    </>
  );

  // 2026-07-20 使用者:「(身分證) 應該是個資?」—— 對。 它原本被放在「薪資 / 帳戶」,
  // 只因為它跟薪轉帳號同樣是加密欄位 —— 那是**存法**的分類, 不是使用者的分類。
  // 身分證是身分資料 → 歸「個資」。
  const nationalIdField = (
    <>
      <Field label="身分證字號" full>
        <div className="text-xs text-slate-500 mb-1 flex items-center gap-2 flex-wrap">
          <span>
            {p?.has_national_id
              ? `目前：${revealed ? (revealed.national_id ?? '—') : p.national_id_mask}（已設定）`
              : '尚未設定'}
          </span>
          {p?.has_national_id && !revealed && (
            <button
              type="button"
              onClick={revealPii}
              disabled={revealing}
              className="text-xs text-slate-600 underline hover:text-slate-900 disabled:opacity-50"
            >
              {revealing ? '讀取中…' : '顯示完整'}
            </button>
          )}
          {revealed && (
            <button
              type="button"
              onClick={() => setRevealed(null)}
              className="text-xs text-slate-500 underline hover:text-slate-800"
            >
              隱藏
            </button>
          )}
        </div>
        {revealErr && <p className="text-xs text-red-600 mb-1">{revealErr}</p>}
        <input
          className={inputCls}
          placeholder="輸入以更新，留空不變"
          value={nationalId}
          onChange={(e) => setNationalId(e.target.value)}
        />
      </Field>
    </>
  );

  // 在職 / 離職證明 (letterhead PDF)。本人 + 人資皆可產。
  const certBlock =
    p && (p.hire_date || p.employee_name) ? (
      <div className="border-t border-slate-100 pt-3">
        <button
          type="button"
          onClick={() => setShowCert((v) => !v)}
          className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
        >
          {showCert ? '收合證明' : p.termination_date ? '🪪 產生離職證明' : '🪪 產生在職證明'}
        </button>
        {showCert && (
          <div className="mt-3">
            <EmploymentCertificateDocument
              name={p.employee_name ?? ''}
              title={p.title_name}
              department={p.department}
              hireDate={p.hire_date}
              terminationDate={p.termination_date}
            />
          </div>
        )}
      </div>
    ) : null;

  // 即時寫入的分頁沒有底部那排「取消 / 儲存」(見下方註解)。 光是把按鈕藏掉還不夠 ——
  // 要明講, 不然使用者會在畫面上找送出鈕、或不確定剛才那筆到底存了沒。
  const instantSaveHint = (
    <p className="text-xs text-slate-500">此分頁的變更會立即生效，不需要另外按「儲存」。</p>
  );

  const title = isHr ? `員工個資 — ${mode.memberName}` : '我的帳戶';

  return (
    <Drawer title={title} onClose={onClose} wide>
      {profileQ.isLoading ? (
        <LoadingPlaceholder />
      ) : (
        <div className="space-y-6">
          {profileQ.error && <ErrorBlock error={profileQ.error} />}
          {err && <div className="text-sm text-red-600">{err}</div>}

          {isHr ? (
            <>
              <p className="text-xs text-slate-500 leading-relaxed">
                人事資料。身分證 / 薪資帳號為加密欄，畫面只顯示遮罩；輸入新值才會更新，留空不變。
              </p>

              {/* 缺件狀態 —— 從成員名冊搬過來 (使用者:「文件那些可以不用這邊 show，
                  但我點下去要一個地方可以看到」)。 放在分頁**之上**: 三件缺的東西
                  分別住在不同分頁 (合約→文件、薪轉→薪資), 收在某一個分頁裡就等於
                  要先猜對才看得到。 */}
              {mode.docStatus?.docExempt ? (
                <p className="text-xs text-slate-500">
                  不需備件
                  {mode.docStatus.docExemptReason ? `：${mode.docStatus.docExemptReason}` : ''}
                  —— 缺件檢查不適用（到職日仍照留，特休年資與薪資不受影響）。
                </p>
              ) : null}
              {mode.docStatus &&
                !mode.docStatus.docExempt &&
                (mode.docStatus.hireDate ? (
                  <div className="flex gap-1 flex-wrap items-center">
                    <span className="text-xs text-slate-500 mr-1">文件 / 投保 / 薪轉</span>
                    <DocBadge ok={mode.docStatus.hasLaborContract} label="合約" />
                    <DocBadge
                      ok={mode.docStatus.hasInsurance}
                      pending={mode.docStatus.insurancePendingFrom}
                      label="投保"
                    />
                    <DocBadge ok={mode.docStatus.hasSalaryAccount} label="薪轉" />
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">非受僱員工（無到職日）— 不適用缺件檢查。</p>
                ))}

              {/* 分頁:個資 / 薪資 / 合約 */}
              <div className="flex gap-1 border-b border-slate-200">
                {(
                  [
                    // 2026-08-22: 「編輯成員」併進來當第一個分頁 (只有拿得到
                    // accountPanel 時才出現 —— 手機唯讀不傳)。
                    ...(accountPanel ? ([['account', '帳號與權限']] as const) : []),
                    ['info', '個資'],
                    ['salary', '薪轉帳戶'],
                    ['docs', '合約 / 文件'],
                  ] as ReadonlyArray<readonly [string, string]>
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setTab(id as typeof tab)}
                    className={`px-3 py-1.5 text-sm -mb-px border-b-2 ${
                      tab === id
                        ? 'border-slate-700 text-slate-800 font-medium'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {tab === 'account' && accountPanel}

              {tab === 'info' && (
                <div className="space-y-6">
                  <Section label="基本個資">{basicFields}</Section>
                  <Section label="身分證字號（加密）">{nationalIdField}</Section>
                  <Section label="雇用資訊">{employmentFields}</Section>
                </div>
              )}
              {/* 2026-07-20 使用者:「勞健保異動 應該不在個資裡面處理啊」—— 對。
                  加保 / 退保 / 調薪是要跟勞保局申報的**人事作業**, 不是個人資料,
                  已搬到 薪資 / Payroll → 投保 分頁 (那裡還有「該調級距的人」提醒)。 */}
              {tab === 'salary' && (
                <div className="space-y-6">
                  {instantSaveHint}
                  {mode.kind === 'hr' && (
                    <Section label="薪轉帳戶（加密）">
                      <EmployeeBankAccounts userId={mode.userId} />
                    </Section>
                  )}
                  <p className="text-xs text-slate-500">
                    勞健保加保 / 退保 / 調保申報請至「薪資 → 投保」。
                  </p>
                </div>
              )}
              {tab === 'docs' && (
                <div className="space-y-6">
                  {instantSaveHint}
                  <EmployeeDocuments userId={mode.userId} />
                  {certBlock}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-6">
              {/* 2026-07-20: 自助模式加分頁 —— 個人選單原本「我的個資」「修改密碼」
                  是兩個入口、兩種浮層 (drawer + modal)。 兩者都是低頻的「關於我」,
                  收進同一張抽屜。 (假勤 / 薪資單維持獨立入口: 使用者說常用, 收進
                  分頁會從 1 點變 2 點。) */}
              <div className="flex gap-1 border-b border-slate-200">
                {(
                  [
                    ['info', '🪪 個資'],
                    ['security', '🔑 安全'],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setTab(id)}
                    className={`px-3 py-1.5 text-sm -mb-px border-b-2 ${
                      tab === id
                        ? 'border-slate-700 text-slate-800 font-medium'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {tab === 'security' ? (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    更新密碼後會自動登出，需用新密碼重新登入。
                  </p>
                  <ChangePasswordForm />
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    你可以自己維護聯絡資料。身分證 / 薪資帳戶等由人事登錄。
                  </p>
                  <Section label="基本個資">{basicFields}</Section>
                  {certBlock}
                </>
              )}
            </div>
          )}

          {/* 這排是**個資表單**的送出鈕 —— submit() 實際只寫 info 分頁那些欄位
              (聯絡資料 / 雇用資訊 / 身分證; salary_bank_* 是 V367 之後沒有輸入框的
              死狀態, 只是原樣回送)。 其他每個分頁都是**即時寫入**且各自有送出鈕:
                · salary   → EmployeeBankAccounts 的「+ 新增薪轉帳戶 / 刪除」直接打 API
                · docs     → EmployeeDocuments 上傳即存
                · security → ChangePasswordForm 自己送出
              2026-08-10 使用者:「怎麼那麼多 button 是要我按哪一個?」—— 而且更糟的是
              「取消」在那些分頁**會說謊**: 剛新增的薪轉帳戶早就寫進伺服器了, 按取消
              不會退回 (實測: 關掉抽屜再開, 帳戶還在)。 原本只排除了 security,
              salary / docs 漏掉。 條件改成白名單, 之後再加分頁預設就是安全的。 */}
          {tab === 'info' && (
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? '儲存中…' : '儲存'}
              </button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {/* 2026-07-20: 原本是 text-xs uppercase 眉標, 比它統領的 text-sm 欄位還小 =
          字級倒置 (鐵則 1)。 指南 2026-06-23 修訂已明講 Section 小標算 heading
          不算 eyebrow → text-sm font-semibold text-slate-700 (§4.4)。 */}
      <h3 className="text-sm font-semibold text-slate-700 mb-2">{label}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
function Field({
  label,
  children,
  full,
  group,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
  /** children 自己就帶 <label> (例:checkbox) → 走 fieldset/legend, 不要巢狀 label。 */
  group?: boolean;
}) {
  // label 必須**包住** children 才有關聯 — 原本是兄弟節點且無 htmlFor, 導致這張
  // 抽屜約 18 個欄位 (含身分證字號 / 薪資帳戶) 在無障礙樹裡全是無名的。
  //
  // 2026-07-20: 這裡原本是把 form/Field 的包法**手抄一份** (同 inputCls 上面那個坑)。
  // 改成直接用共用元件 — DOM 完全相同 (labelCls 兩邊是同一個字串, 且不傳
  // required/hint/error 就不會多渲染任何節點), 但這種「wrapper 包 children」的
  // 關聯方式只有一份實作要維護。 18 個呼叫點的寫法不變。
  return (
    <SharedField label={label} className={full ? 'sm:col-span-2' : ''} group={group}>
      {children}
    </SharedField>
  );
}

// ----- 薪轉帳戶 (V367) ----------------------------------------------------
// 原本是 employee_profile 的單一內嵌欄, 升成多帳戶 + 一個主要 —— 跟記帳主體帳戶、
// 投資者收款帳戶用同一組輸入欄位 (BankFields)。 薪資試算讀「主要」那筆。
// 帳號送出即加密, 畫面只顯示遮罩 (跟身分證同一套 PII 處理)。
interface EmployeeBankAccount {
  id: string;
  bank_name: string | null;
  branch_name: string | null;
  account_holder_name: string | null;
  account_number_mask: string | null;
  is_default: boolean;
  note: string | null;
}

function EmployeeBankAccounts({ userId }: { userId: number }) {
  const actor = useActor((s) => s.actor);
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  // 2026-08-25: 編輯已存在的帳戶。 null = 沒在編輯; 有值 = 正在編輯那一筆的 id。
  // 帳號欄留空 = 不變更 (加密欄, 同個資分頁的身分證慣例)。
  const [editingId, setEditingId] = useState<string | null>(null);
  const [bank, setBank] = useState<BankFieldsValue>({
    currency: 'TWD',
    bank_name: '',
    branch_name: '',
    account_holder_name: '',
    account_number: '',
  });
  const [isDefault, setIsDefault] = useState(false);

  const key = ['member-bank-accounts', userId];
  const q = useQuery<EmployeeBankAccount[], ApiError>({
    queryKey: key,
    queryFn: () => api<EmployeeBankAccount[]>(`/api/v1/members/${userId}/bank-accounts`, { actor }),
  });
  const rows = q.data ?? [];
  // 2026-09-12 使用者:「存完之後上面那個『薪轉 缺』紅標沒更新, 關掉重開還是紅的」。
  // 缺件三顆標 (合約 / 投保 / 薪轉) 讀的是**名冊那一列** (/api/v1/users 的
  // has_salary_account), 不是這裡的帳戶清單 —— 只 invalidate 自己這支查詢, 標籤
  // 當然不會動。 (同一類: 2026-09-06 加保完「待處理」不更新, 也是少 invalidate
  // 另一支查詢。) 新增 / 改 / 設主要 / 刪除 都會改變 has_salary_account, 所以
  // 掛在共用的 done()。
  const done = async () => {
    await qc.invalidateQueries({ queryKey: key });
    await qc.invalidateQueries({ queryKey: ['users'] });
  };

  const add = useMutation<unknown, ApiError, void>({
    mutationFn: () =>
      api(`/api/v1/members/${userId}/bank-accounts`, {
        actor,
        method: 'POST',
        json: {
          bank_name: bank.bank_name.trim(),
          branch_name: bank.branch_name.trim() || undefined,
          account_holder_name: bank.account_holder_name.trim(),
          account_number: normalizeAccountNumber(bank.account_number),
          is_default: isDefault || rows.length === 0,
        },
      }),
    onSuccess: () => {
      closeForm();
      done();
    },
  });

  const blankBank = (): BankFieldsValue => ({
    currency: 'TWD',
    bank_name: '',
    branch_name: '',
    account_holder_name: '',
    account_number: '',
  });
  const closeForm = () => {
    setAdding(false);
    setEditingId(null);
    setIsDefault(false);
    setBank(blankBank());
  };
  const startEdit = (r: EmployeeBankAccount) => {
    setAdding(false);
    setEditingId(r.id);
    setIsDefault(r.is_default);
    setBank({
      currency: 'TWD',
      bank_name: r.bank_name ?? '',
      branch_name: r.branch_name ?? '',
      account_holder_name: r.account_holder_name ?? '',
      account_number: '', // 加密欄讀不回明文 —— 留空代表不變更
    });
  };
  const editingRow = rows.find((r) => r.id === editingId) ?? null;

  // 只送有改的欄位; 帳號留空就不帶 (後端據此判斷「不變更」)。
  const save = useMutation<unknown, ApiError, void>({
    mutationFn: () => {
      const acct = normalizeAccountNumber(bank.account_number);
      return api(`/api/v1/members/${userId}/bank-accounts/${editingId}`, {
        actor,
        method: 'PATCH',
        json: {
          bank_name: bank.bank_name.trim(),
          branch_name: bank.branch_name.trim() || null,
          account_holder_name: bank.account_holder_name.trim(),
          ...(acct ? { account_number: acct } : {}),
        },
      });
    },
    onSuccess: () => {
      closeForm();
      done();
    },
  });

  const setPrimary = useMutation<unknown, ApiError, string>({
    mutationFn: (id) =>
      api(`/api/v1/members/${userId}/bank-accounts/${id}`, {
        actor,
        method: 'PATCH',
        json: { is_default: true },
      }),
    onSuccess: done,
  });

  const remove = useMutation<unknown, ApiError, string>({
    mutationFn: (id) =>
      api(`/api/v1/members/${userId}/bank-accounts/${id}`, { actor, method: 'DELETE' }),
    onSuccess: done,
  });

  // 編輯時帳號可留空 (= 不變更); 有填就得是合法格式。 新增時一律必填。
  const acctOk = editingId
    ? !bank.account_number.trim() || isValidAccountNumber(bank.account_number)
    : isValidAccountNumber(bank.account_number);
  const valid = bank.bank_name.trim() && bank.account_holder_name.trim() && acctOk;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        薪資試算會匯入標示「主要」的帳戶。帳號送出即加密，畫面僅顯示末四碼。
      </p>
      {q.error && <ErrorBlock error={q.error} />}
      {(add.error || save.error || setPrimary.error || remove.error) && (
        <ErrorBlock error={add.error ?? save.error ?? setPrimary.error ?? remove.error} />
      )}

      {rows.length > 0 && (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              {/* DESIGN_GUIDE §3.3 列表最多兩行: 主行 (銀行 + 帳號 + 徽章) 不換行, 各行截斷 + title */}
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="text-slate-800 truncate"
                    title={[r.bank_name, r.branch_name].filter(Boolean).join(' ') || undefined}
                  >
                    {[r.bank_name, r.branch_name].filter(Boolean).join(' ') || '—'}
                  </span>
                  <span className="ml-0.5 font-mono text-slate-500 shrink-0">
                    {r.account_number_mask}
                  </span>
                  {r.is_default && (
                    <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 ring-1 ring-blue-200">
                      主要
                    </span>
                  )}
                </div>
                <span
                  className="block text-xs text-slate-400 truncate"
                  title={r.account_holder_name ?? undefined}
                >
                  {r.account_holder_name}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => startEdit(r)}
                  className="text-xs px-2 py-0.5 border border-slate-300 rounded bg-white hover:bg-slate-50"
                >
                  編輯
                </button>
                {!r.is_default && (
                  <button
                    type="button"
                    onClick={() => setPrimary.mutate(r.id)}
                    disabled={setPrimary.isPending}
                    className="text-xs px-2 py-0.5 border border-slate-300 rounded bg-white hover:bg-slate-50"
                  >
                    設為主要
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove.mutate(r.id)}
                  disabled={remove.isPending}
                  className="text-xs px-2 py-0.5 border border-red-200 text-red-600 rounded bg-white hover:bg-red-50"
                >
                  刪除
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {rows.length === 0 && !q.isLoading && !adding && (
        <p className="text-sm text-slate-500">尚未登記薪轉帳戶。</p>
      )}

      {adding || editingId ? (
        <div className="border-t border-slate-100 pt-3 space-y-3">
          {editingId && (
            <p className="text-xs text-slate-500">
              編輯「{[editingRow?.bank_name, editingRow?.branch_name].filter(Boolean).join(' ')}
              」。帳號留空就不會變更。
            </p>
          )}
          <BankFields
            value={bank}
            onChange={(patch) => setBank((prev) => ({ ...prev, ...patch }))}
            showCurrency={false}
            accountRequired={!editingId}
            accountPlaceholder={
              editingId ? `留空不變更（目前 ${editingRow?.account_number_mask ?? '—'}）` : undefined
            }
            accountHint={editingId ? '要換帳號才填；留空維持原本的' : '送出即加密，僅保留遮罩顯示'}
          />
          {!editingId && rows.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-slate-300"
              />
              設為主要帳戶（薪資匯入此帳戶）
            </label>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeForm}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
            >
              取消
            </button>
            <button
              type="button"
              onClick={() => (editingId ? save.mutate() : add.mutate())}
              disabled={!valid || add.isPending || save.isPending}
              className="px-4 py-1.5 text-sm bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50"
            >
              {add.isPending || save.isPending ? '儲存中…' : editingId ? '儲存變更' : '新增帳戶'}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded hover:bg-slate-800"
        >
          + 新增薪轉帳戶
        </button>
      )}
    </div>
  );
}
